import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { useUnistyles } from 'react-native-unistyles';
import { useRouter } from 'expo-router';
import SelectInput from '@/components/General/inputs/SelectInput';
import Unicon from '@/components/General/Unicon';
import LinkModal from '@/components/General/LinkModal';
import { TRIGGER_LABELS, TRIGGER_DESCRIPTIONS, TRIGGER_OPTIONS, FONT, TRIGGER_HEIGHT, NODE_WIDTH } from '../constants';

export default function TriggerNode({ data, selected }) {
    const { theme } = useUnistyles();
    const router = useRouter();
    const triggerConfig = data.triggerConfig || {};
    const triggerType = triggerConfig.triggerType || 'MANUAL';
    const isDark = theme.scheme === 'dark';

    const isCompleted = data.isCompleted;
    const highlightColor = isCompleted ? '#10b981' : null;
    const highlightBg = isCompleted ? '#10b98110' : null;

    const description = TRIGGER_DESCRIPTIONS[triggerType];
    const triggeringForms = data.triggeringForms || [];
    const triggeringEvents = data.triggeringEvents || [];
    const attendanceConfig = triggerConfig.triggerConfig || {};
    const attendanceModeLabel = attendanceConfig.mode === 'ATTENDED' ? 'Attended' : 'Missed';
    const attendanceWeeks = attendanceConfig.weeks;
    const attendanceRoleID = attendanceConfig.roleID;
    const attendanceRoleName = attendanceRoleID
        ? (data.actionLookups?.roles || []).find(r => r.id === attendanceRoleID)?.name
        : null;

    return (
        <div style={{
            background: highlightBg || (isDark ? theme.colors.surface : '#fff'),
            border: `1.5px solid ${highlightColor || theme.colors.border}`,
            borderRadius: 14,
            minWidth: NODE_WIDTH,
            maxWidth: NODE_WIDTH,
            height: TRIGGER_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: highlightColor
                ? `0 0 0 2px ${highlightColor}20, 0 4px 20px rgba(0,0,0,0.12)`
                : '0 2px 12px rgba(0,0,0,0.07)',
            overflow: 'visible',
            fontFamily: FONT,
            position: 'relative',
            outline: selected ? `2px solid ${theme.colors.primary}` : 'none',
            outlineOffset: '-2px',
        }}>
            <Handle
                type="source"
                position={Position.Bottom}
                id="out"
                isConnectableStart={!data.readOnly}
                isConnectableEnd={false}
                style={{
                    background: theme.colors.primary,
                    width: 10,
                    height: 10,
                    border: `2px solid ${isDark ? theme.colors.surface : '#fff'}`,
                    bottom: -5,
                }}
            />

            <div style={{
                padding: '10px 14px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                borderTop: `3px solid ${theme.colors.primary}`,
                borderRadius: '10px 10px 0 0',
                borderBottom: `1px solid ${theme.colors.border}`,
            }}>
                <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: theme.colors.primaryContainer,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    flexShrink: 0,
                }}>
                    <Unicon name="play-outline" size={16} color={theme.colors.primary} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.primary, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Trigger
                </span>
                {isCompleted && (
                    <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: '#10b981', textTransform: 'uppercase' }}>
                        Done
                    </span>
                )}
            </div>

            <div style={{ padding: '10px 14px' }}>
                {data.readOnly ? (
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                        {TRIGGER_LABELS[triggerType] || 'Manual'}
                    </div>
                ) : (
                    <SelectInput
                        title="Type"
                        placeholder="Select trigger"
                        value={triggerType}
                        options={data.triggerOptions || TRIGGER_OPTIONS}
                        onUpdate={(value) => data.onUpdateTrigger?.(value, triggerConfig)}
                    />
                )}

                {!data.readOnly && data.onConfigureTrigger && (
                    <div
                        className="nodrag"
                        data-testid="trigger-configure-button"
                        onClick={() => data.onConfigureTrigger()}
                        style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: theme.colors.primary, cursor: 'pointer', fontFamily: FONT }}
                    >
                        Configure trigger
                    </div>
                )}

                {description && (
                    <div style={{ marginTop: 8, borderTop: `1px solid ${theme.colors.border}`, paddingTop: 8 }}>
                        <div
                            style={{ fontSize: 11, color: theme.colors.textSecondary, lineHeight: 1.4 }}
                            dangerouslySetInnerHTML={{ __html: description }}
                        />
                    </div>
                )}

                {triggerType === 'FORM_ENTRY' && (
                    <div data-testid="path-triggered-by" style={{ marginTop: 6 }}>
                        <LinkModal
                            linkText={triggeringForms.length > 0
                                ? `View linked forms (${triggeringForms.length})`
                                : 'View linked forms'}
                            iconName="document-text-outline"
                            modalTitle="Linked Forms"
                            testID="path-triggered-by-link"
                        >
                            <div style={{ marginBottom: 8, fontSize: 12, color: theme.colors.textSecondary, lineHeight: 1.4, fontFamily: FONT }}>
                                Forms that start runs for submitters in this automation. Managed in each form&rsquo;s Settings &rarr; Trigger Automation.
                            </div>
                            {triggeringForms.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
                                    {triggeringForms.map((form, i) => (
                                        <div
                                            key={form.id}
                                            className="nodrag"
                                            data-testid={`path-triggered-by-form-${form.id}`}
                                            onClick={() => router.push(`/forms/${form.id}`)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '8px 2px',
                                                cursor: 'pointer',
                                                fontFamily: FONT,
                                                borderBottom: i < triggeringForms.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                                            }}
                                        >
                                            <Unicon name="document-text-outline" size={14} color={theme.colors.textSecondary} />
                                            <span style={{ fontSize: 13, fontWeight: 400, color: theme.colors.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FONT }}>
                                                {form.title}
                                            </span>
                                            {form.status !== 'published' && (
                                                <span style={{
                                                    fontSize: 9,
                                                    fontWeight: 600,
                                                    color: theme.colors.textSecondary,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.3,
                                                    fontFamily: FONT,
                                                }}>
                                                    {form.status}
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ fontSize: 13, color: theme.colors.textTertiary, fontStyle: 'italic', fontFamily: FONT }}>
                                    No forms linked yet &mdash; link one from the form&rsquo;s Settings tab.
                                </div>
                            )}
                        </LinkModal>
                    </div>
                )}

                {triggerType === 'EVENT_CHECK_IN' && (
                    <div data-testid="path-triggered-by-events" style={{ marginTop: 6 }}>
                        <LinkModal
                            linkText={triggeringEvents.length > 0
                                ? `View linked events (${triggeringEvents.length})`
                                : 'View linked events'}
                            iconName="calendar-outline"
                            modalTitle="Linked Events"
                            testID="path-triggered-by-events-link"
                        >
                            <div style={{ marginBottom: 8, fontSize: 12, color: theme.colors.textSecondary, lineHeight: 1.4, fontFamily: FONT }}>
                                Events that start runs for attendees in this automation when they check in. Managed in each event instance&rsquo;s Check-in Automation setting.
                            </div>
                            {triggeringEvents.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
                                    {triggeringEvents.map((event, i) => (
                                        <div
                                            key={event.id}
                                            className="nodrag"
                                            data-testid={`path-triggered-by-event-${event.id}`}
                                            onClick={() => router.push(`/events/${event.id}`)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '8px 2px',
                                                cursor: 'pointer',
                                                fontFamily: FONT,
                                                borderBottom: i < triggeringEvents.length - 1 ? `1px solid ${theme.colors.border}` : 'none',
                                            }}
                                        >
                                            <Unicon name="calendar-outline" size={14} color={theme.colors.textSecondary} />
                                            <span style={{ fontSize: 13, fontWeight: 400, color: theme.colors.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: FONT }}>
                                                {event.name}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ fontSize: 13, color: theme.colors.textTertiary, fontStyle: 'italic', fontFamily: FONT }}>
                                    No events linked yet &mdash; link one from the event instance&rsquo;s Check-in Automation setting.
                                </div>
                            )}
                        </LinkModal>
                    </div>
                )}

                {triggerType === 'ATTENDANCE' && (
                    <div data-testid="path-attendance-summary" style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.colors.text, fontFamily: FONT }}>
                            <Unicon name="people-outline" size={13} color={theme.colors.textSecondary} />
                            <span style={{ fontWeight: 600 }}>{attendanceModeLabel}</span>
                            {attendanceWeeks ? <span style={{ color: theme.colors.textSecondary }}>&middot; {attendanceWeeks} Week{attendanceWeeks === 1 ? '' : 's'}</span> : null}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: theme.colors.textSecondary, fontFamily: FONT }}>
                            <Unicon name="pricetag-outline" size={12} color={theme.colors.textTertiary} />
                            <span>{attendanceRoleID ? (attendanceRoleName || 'Specific role') : 'Anyone'}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
